import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Edit3, Sparkles, HeartHandshake, Wand2, Upload } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const AI_STYLES = [
  { id: "watercolor", emoji: "🖌️", label: "Watercolor", prompt: "Soft watercolor portrait with gentle brushstrokes, warm and loving feeling. Square." },
  { id: "cartoon",    emoji: "🎨", label: "Cartoon",    prompt: "Friendly cartoon portrait with warm pastel colours and a kind expression. Square." },
  { id: "vintage",    emoji: "📷", label: "Vintage",    prompt: "Beautiful 1970s family-photo portrait, sepia warmth, gentle film grain. Square." },
  { id: "oil",        emoji: "🖼️", label: "Oil Paint",  prompt: "Classic oil painting portrait with rich warm colours, gallery quality. Square." },
  { id: "studio",     emoji: "💼", label: "Studio",     prompt: "Beautiful studio portrait, soft warm lighting, gentle smile. Square." },
  { id: "anime",      emoji: "🌸", label: "Anime",      prompt: "Anime-style portrait, soft cel-shading, kind warm expression. Square." },
];

const PROMPT_IDEAS = [
  "An older lady with kind grey curls, warm smile, watercolor portrait",
  "A grandfather playing chess in a cardigan, oil painting portrait",
  "A loving family dog with golden fur, cartoon portrait",
  "A young grandchild laughing in a sunny garden, soft pastel illustration",
];

export default function FamilyFriendsPage() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);  // for create/edit modal
  const [studio, setStudio] = useState(null);    // currently-open photo studio target
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { setRows((await api.get("/family-friends")).data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!confirm("Remove this person from your gallery?")) return;
    await api.delete(`/family-friends/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-5 max-w-6xl" data-testid="family-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl grid place-items-center text-white shrink-0"
               style={{ background: "linear-gradient(135deg, hsl(var(--coral)), hsl(var(--sun)))" }}>
            <HeartHandshake className="w-6 h-6" strokeWidth={2.4} />
          </div>
          <div>
            <h1 className="text-4xl font-display font-black tracking-tight">Family &amp; Friends</h1>
            <p className="text-muted-foreground">A warm gallery of the people you love. Add photos, AI-style them, keep them close.</p>
          </div>
        </div>
        <Button onClick={() => { setEditing({ name: "", relationship: "", notes: "" }); setOpen(true); }}
          data-testid="add-loved-one-btn"
          className="rounded-full min-h-[48px] font-bold gap-2 text-white"
          style={{ background: "linear-gradient(135deg, hsl(var(--coral)), hsl(var(--sun)))" }}>
          <Plus className="w-4 h-4" /> Add a loved one
        </Button>
      </header>

      {rows.length === 0 ? (
        <Card className="rounded-3xl p-10 border-2 text-center">
          <div className="text-6xl mb-2">💛</div>
          <h3 className="font-display font-extrabold text-2xl">Your gallery is empty</h3>
          <p className="text-muted-foreground mt-1 max-w-md mx-auto">Add the people who mean the most to you — partners, kids, grandkids, friends, even pets. Every face is a small reminder you're loved.</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((p) => (
            <FriendCard key={p.id} p={p}
              onEdit={() => { setEditing(p); setOpen(true); }}
              onPhoto={() => setStudio(p)}
              onDelete={() => onDelete(p.id)} />
          ))}
        </div>
      )}

      <PersonDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={() => { setOpen(false); load(); }} />
      {studio && <PhotoStudioDialog person={studio} onClose={() => setStudio(null)} onSaved={load} />}
    </div>
  );
}

function FriendCard({ p, onEdit, onPhoto, onDelete }) {
  const gradients = [
    "linear-gradient(135deg, #EE82A2, #F4A261)",
    "linear-gradient(135deg, #48CAE4, #1E6091)",
    "linear-gradient(135deg, #7B5EA7, #EE82A2)",
    "linear-gradient(135deg, #F4A261, #E76F51)",
    "linear-gradient(135deg, #5A8A4A, #C97B53)",
  ];
  const grad = gradients[(p.id || "x").charCodeAt(0) % gradients.length];
  return (
    <Card className="rounded-3xl border-2 overflow-hidden card-lift" data-testid={`friend-card-${p.id}`}>
      <div className="aspect-square relative" style={{ background: p.photo_url ? "transparent" : grad }}>
        {p.photo_url
          ? <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" />
          : <div className="absolute inset-0 grid place-items-center text-white font-display font-black text-6xl">{(p.name || "?")[0]}</div>}
        {p.ai_photos?.length > 0 && (
          <span className="absolute top-2 right-2 rounded-full bg-white/95 text-coral px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> {p.ai_photos.length} AI
          </span>
        )}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-display font-extrabold text-lg leading-tight truncate">{p.name}</h3>
          {p.relationship && <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground shrink-0">{p.relationship}</span>}
        </div>
        {p.notes && <p className="text-sm text-foreground/80 line-clamp-2">{p.notes}</p>}
        <div className="flex gap-1.5 pt-1 flex-wrap">
          <Button size="sm" className="rounded-full font-bold gap-1 text-white"
            style={{ background: "linear-gradient(135deg, hsl(var(--coral)), hsl(var(--sun)))" }}
            onClick={onPhoto} data-testid={`friend-studio-${p.id}`}>
            <Sparkles className="w-3.5 h-3.5" /> Photo Studio
          </Button>
          <Button size="sm" variant="outline" className="rounded-full font-bold border-2" onClick={onEdit} data-testid={`friend-edit-${p.id}`}>
            <Edit3 className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" className="rounded-full text-destructive font-bold ml-auto" onClick={onDelete} data-testid={`friend-delete-${p.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

function PersonDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState({ name: "", relationship: "", notes: "" });
  useEffect(() => { setForm(editing || { name: "", relationship: "", notes: "" }); }, [editing, open]);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    try {
      if (editing?.id) await api.put(`/family-friends/${editing.id}`, form);
      else await api.post("/family-friends", form);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="friend-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{editing?.id ? "Edit" : "Add"} loved one</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Name</Label>
            <Input value={form.name} onChange={upd("name")} className="rounded-xl min-h-[48px]" placeholder="e.g. Mum" data-testid="friend-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Relationship</Label>
            <Input value={form.relationship} onChange={upd("relationship")} className="rounded-xl min-h-[48px]" placeholder="e.g. Mother, Best friend, Pet" data-testid="friend-rel-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Notes (optional)</Label>
            <Textarea value={form.notes} onChange={upd("notes")} rows={3} className="rounded-xl" placeholder="A short happy memory…" data-testid="friend-notes-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-full font-bold" style={{ background: "hsl(var(--client))" }} data-testid="friend-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhotoStudioDialog({ person, onClose, onSaved }) {
  const [tab, setTab] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(person.photo_url || "");
  const [history, setHistory] = useState(person.ai_photos || []);
  const [prompt, setPrompt] = useState(PROMPT_IDEAS[0]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.put(`/family-friends/${person.id}/photo`, { image_b64: reader.result });
        setPhoto(reader.result);
        toast.success("Photo saved");
        onSaved();
      } catch (err) { toast.error(formatApiError(err)); }
    };
    reader.readAsDataURL(file);
  };

  const aiEdit = async (overridePrompt) => {
    if (!photo) { toast.error("Upload a photo first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/family-friends/${person.id}/photo/ai-edit`, { prompt: overridePrompt || prompt });
      setPhoto(data.url);
      setHistory((h) => [{ url: data.url, prompt: overridePrompt || prompt, at: new Date().toISOString() }, ...h]);
      toast.success("AI photo created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const aiGenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/family-friends/${person.id}/photo/ai-generate`, { prompt });
      setPhoto(data.url);
      setHistory((h) => [{ url: data.url, prompt, at: new Date().toISOString() }, ...h]);
      toast.success("Conjured!");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const setAsMain = async (url) => {
    await api.put(`/family-friends/${person.id}/photo`, { image_b64: url });
    setPhoto(url); toast.success("Set as main"); onSaved();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="friend-studio-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-coral" /> Photo Studio — {person.name}</DialogTitle></DialogHeader>

        <div className="rounded-2xl border-2 border-dashed p-3 grid place-items-center bg-muted/30 min-h-[260px] relative overflow-hidden mb-4">
          {photo
            ? <img src={photo} alt={person.name} className="rounded-xl max-h-[320px]" data-testid="friend-photo-preview" />
            : <p className="text-sm text-muted-foreground">No photo yet</p>}
          {busy && <div className="absolute inset-0 bg-black/50 grid place-items-center text-white"><div className="text-center"><div className="w-10 h-10 rounded-full border-4 border-white border-t-transparent animate-spin mx-auto" /><p className="mt-2 text-sm font-bold">Cooking AI magic…</p></div></div>}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-full p-1 h-12">
            <TabsTrigger value="upload" className="rounded-full gap-1.5"><Upload className="w-4 h-4" /> Upload &amp; Style</TabsTrigger>
            <TabsTrigger value="conjure" className="rounded-full gap-1.5"><Wand2 className="w-4 h-4" /> Conjure with AI</TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-3 mt-4">
            <Input type="file" accept="image/*" onChange={upload} className="rounded-xl" data-testid="friend-upload-input" />
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">One-tap AI styles</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {AI_STYLES.map((st) => (
                  <Button key={st.id} variant="outline" disabled={busy || !photo} onClick={() => aiEdit(st.prompt)}
                    data-testid={`friend-ai-${st.id}`}
                    className="rounded-2xl h-auto py-3 flex flex-col gap-1 border-2 hover:border-coral/50 hover:bg-coral/5">
                    <span className="text-2xl">{st.emoji}</span>
                    <span className="font-bold text-xs">{st.label}</span>
                  </Button>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="conjure" className="space-y-3 mt-4">
            <p className="text-sm text-muted-foreground">Describe a portrait — Gemini Nano Banana paints it from scratch. No photo needed.</p>
            <div className="flex flex-wrap gap-1.5">
              {PROMPT_IDEAS.map((p, i) => (
                <button key={i} type="button" onClick={() => setPrompt(p)}
                  className="rounded-full bg-muted hover:bg-coral/10 text-xs font-semibold px-3 py-1.5 border" data-testid={`friend-idea-${i}`}>{p.slice(0, 40)}…</button>
              ))}
            </div>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} className="rounded-xl" data-testid="friend-prompt-input" />
            <Button onClick={aiGenerate} disabled={busy} className="rounded-full font-bold gap-2 min-h-[48px] text-white"
              style={{ background: "linear-gradient(135deg, hsl(var(--coral)), hsl(var(--sun)))" }} data-testid="friend-conjure-btn">
              <Wand2 className="w-4 h-4" /> {busy ? "Conjuring…" : "Conjure portrait"}
            </Button>
          </TabsContent>
        </Tabs>

        {history.length > 0 && (
          <div className="mt-4">
            <Label className="text-xs uppercase font-bold tracking-wider mb-2 block">Photo history</Label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-44 overflow-y-auto">
              {history.map((h, idx) => (
                <button key={idx} type="button" onClick={() => setAsMain(h.url)}
                  className="relative group rounded-xl overflow-hidden border-2 hover:border-client" data-testid={`friend-history-${idx}`}>
                  <img src={h.url} alt={h.prompt} className="w-full aspect-square object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 grid place-items-center transition-colors">
                    <span className="opacity-0 group-hover:opacity-100 text-white text-[10px] font-bold">Use as main</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
